import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, ChevronRight, X, AlertTriangle, Store, RefreshCw, CheckCircle2, CreditCard, Banknote, Landmark, Mail } from "lucide-react";
import { api, formatApiError, gbp } from "@/lib/api";
import RetailerLayout from "@/components/RetailerLayout";

const ORDER_STATUSES = ["pending_payment", "paid", "partially_refunded", "refunded", "cancelled", "expired"];
const PAY_STYLE = {
  paid: "bg-emerald-100 text-emerald-800",
  pending: "bg-amber-100 text-amber-800",
  partially_refunded: "bg-orange-100 text-orange-800",
  refunded: "bg-slate-200 text-slate-700",
  expired: "bg-slate-200 text-slate-500",
  cancelled: "bg-red-100 text-red-700",
};
const TRANSFER_STYLE = {
  paid: "bg-emerald-100 text-emerald-800",
  pending: "bg-amber-100 text-amber-800",
  failed: "bg-red-100 text-red-700",
  failed_permanent: "bg-red-200 text-red-800",
  reversed: "bg-slate-200 text-slate-600",
  skipped: "bg-slate-100 text-slate-500",
};
const TRANSFER_LABEL = {
  paid: "In retailer's Stripe balance",
  pending: "Transfer pending",
  failed: "Transfer failed",
  failed_permanent: "Transfer failed (permanent)",
  reversed: "Reversed (refunded)",
  skipped: "Skipped",
};

export default function AdminOrders() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [needsAttention, setNeedsAttention] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (status) params.status = status;
    if (needsAttention) params.needs_attention = true;
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    try {
      const r = await api.get("/admin/orders", { params });
      setRows(r.data.orders || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [q, status, needsAttention, from, to]);

  useEffect(() => { load(); }, [status, needsAttention, from, to]); // eslint-disable-line

  const attentionCount = rows.filter((o) => o.needs_attention).length;

  return (
    <RetailerLayout>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="admin-orders-page">
        <h1 className="font-display font-bold text-2xl text-slate-900 mb-1">Combined orders</h1>
        <p className="text-sm text-slate-500 mb-4">Multi-retailer orders, payment status and retailer transfers.</p>

        <button
          onClick={() => setNeedsAttention((v) => !v)}
          className={`w-full sm:w-auto mb-4 inline-flex items-center gap-2 rounded-xl px-4 py-2.5 font-semibold border transition-colors ${
            needsAttention ? "bg-red-600 text-white border-red-600" : "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
          }`}
          data-testid="filter-needs-attention"
        >
          <AlertTriangle className="w-4 h-4" />
          {needsAttention ? "Showing: Needs attention" : "Show orders that need attention"}
          {!needsAttention && attentionCount > 0 && <span className="ml-1 bg-red-600 text-white text-xs rounded-full px-2 py-0.5">{attentionCount}</span>}
        </button>

        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5 grid sm:grid-cols-4 gap-3">
          <div className="relative sm:col-span-2">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input className="fm-input pl-10" placeholder="Order reference, customer name or email…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="order-search" />
          </div>
          <select className="fm-input" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="order-filter-status">
            <option value="">All statuses</option>
            {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <button onClick={load} className="h-11 rounded-xl bg-[#0F3D2E] text-white font-semibold" data-testid="order-search-btn">Search</button>
          <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-2">
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500 shrink-0 w-10 sm:w-auto">From</label>
              <input type="date" className="fm-input flex-1 min-w-0" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="order-filter-from" />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500 shrink-0 w-10 sm:w-auto">To</label>
              <input type="date" className="fm-input flex-1 min-w-0" value={to} onChange={(e) => setTo(e.target.value)} data-testid="order-filter-to" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {loading ? <div className="p-8 text-slate-500">Loading…</div>
            : rows.length === 0 ? <div className="p-8 text-center text-slate-500" data-testid="orders-empty">No combined orders match.</div>
              : (
                <ul className="divide-y divide-slate-100" data-testid="orders-list">
                  {rows.map((o) => (
                    <li key={o.id}>
                      <button onClick={() => setSelected(o)} className="w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-50" data-testid={`order-row-${o.reference}`}>
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900 truncate flex items-center gap-2">
                            {o.needs_attention && <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" data-testid={`order-attention-${o.reference}`} />}
                            <span className="font-mono">{o.reference}</span>
                            <span className="text-slate-400 font-normal truncate">· {o.customer_name || o.customer_email}</span>
                          </div>
                          <div className="text-xs text-slate-500 truncate">{gbp(o.total_gbp)} · {(o.retailer_groups || []).length} retailer(s) · {new Date(o.created_at).toLocaleDateString("en-GB")}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${PAY_STYLE[o.payment_status] || "bg-slate-100"}`}>{(o.payment_status || "").replace(/_/g, " ")}</span>
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
        </div>
      </main>
      {selected && <OrderDetail order={selected} onClose={() => setSelected(null)} onChanged={(u) => { setSelected(u); load(); }} />}
    </RetailerLayout>
  );
}

function OrderDetail({ order, onClose, onChanged }) {
  const [o, setO] = useState(order);
  const [retrying, setRetrying] = useState(null); // retailer_id currently retrying
  const [refunding, setRefunding] = useState(null); // "full" | retailer_id

  async function doRefund(scope) {
    if (refunding) return; // duplicate-click guard
    const isFull = scope === "full";
    const label = isFull ? `the FULL order (${gbp(o.total_gbp)})` : `this retailer's lines`;
    if (!window.confirm(`Refund ${label}? This refunds the customer in Stripe (TEST mode) and reverses the matching transfer(s). This cannot be undone.`)) return;
    setRefunding(scope);
    try {
      const body = isFull ? {} : { retailer_id: scope };
      const r = await api.post(`/admin/orders/${o.id}/refund`, body);
      toast.success(`Refunded ${gbp(r.data.refunded_gbp)} (${r.data.scope}).`);
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRefunding(null); }
  }

  async function refresh() {
    try {
      const r = await api.get("/admin/orders", { params: { q: o.reference } });
      const fresh = (r.data.orders || []).find((x) => x.id === o.id);
      if (fresh) { setO(fresh); onChanged(fresh); }
    } catch (e) { /* non-fatal */ }
  }

  async function retry(retailerId) {
    if (retrying) return; // guard against repeated clicks
    setRetrying(retailerId);
    try {
      const r = await api.post(`/admin/orders/${o.id}/retry-transfer`, { retailer_id: retailerId });
      if (r.data.ok) toast.success("Transfer recovered — funds are in the retailer's Stripe balance.");
      else toast.error("Transfer still failing. It stays queued for automatic retry.");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRetrying(null); }
  }

  const names = o.retailer_names || {};
  const groups = (o.retailer_groups || []).map((g) => {
    const items = (o.items || []).filter((i) => i.retailer_id === g.retailer_id);
    const sale = items.reduce((s, i) => s + Number(i.price_gbp || 0), 0);
    const commission = items.reduce((s, i) => s + Number(i.commission_gbp || 0), 0);
    return { ...g, items, sale, commission };
  });
  const emailResults = o.email_results || [];

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-end" role="dialog" aria-modal="true" data-testid="order-detail-drawer" onClick={onClose}>
      <div className="bg-white w-full max-w-md h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white px-5 py-4 border-b border-slate-200 flex items-center justify-between z-10">
          <div>
            <h3 className="font-display font-semibold text-lg text-slate-900 font-mono">{o.reference}</h3>
            <p className="text-xs text-slate-500">{o.customer_name || "—"} · {o.customer_email}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="close-order-detail"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-5">
          {o.needs_attention && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2 text-sm text-red-800" data-testid="attention-banner">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>This order needs attention — one or more retailer transfers failed. Use "Retry transfer" below.</span>
            </div>
          )}

          {/* Status rows — customer payment vs retailer transfer vs bank payout */}
          <section className="space-y-2">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</h4>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600 flex items-center gap-1.5"><CreditCard className="w-4 h-4 text-slate-400" /> Customer payment</span>
              <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${PAY_STYLE[o.payment_status] || "bg-slate-100"}`} data-testid="customer-payment-status">{(o.payment_status || "").replace(/_/g, " ")}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600 flex items-center gap-1.5"><Banknote className="w-4 h-4 text-slate-400" /> Retailer transfers</span>
              <span className="text-xs text-slate-500">into Stripe balance (per retailer below)</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600 flex items-center gap-1.5"><Landmark className="w-4 h-4 text-slate-400" /> Bank payout</span>
              <span className="text-xs text-slate-500">separate — each retailer's own payout schedule</span>
            </div>
          </section>

          {/* Totals */}
          <section className="bg-slate-50 rounded-xl p-3 text-sm space-y-1">
            <Row k="Order total (customer paid)" v={gbp(o.total_gbp)} bold />
            {o.stripe_fee_gbp != null ? (
              <Row k="Actual Stripe fee (platform absorbs)" v={gbp(o.stripe_fee_gbp)} muted />
            ) : (
              <div className="flex items-center justify-between" data-testid="order-fee-pending">
                <span className="text-slate-500">Actual Stripe fee (platform absorbs)</span>
                <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${o.fee_status === "unknown" ? "bg-amber-100 text-amber-800" : "bg-slate-200 text-slate-600"}`}>
                  {o.fee_status === "unknown" ? "Unknown — not reported by Stripe" : "Pending — settling"}
                </span>
              </div>
            )}
            <p className="text-[11px] text-slate-400 pt-1">Commission is shown before fees and is not FloorMatch profit.</p>
          </section>

          {/* Grouped by retailer */}
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">By retailer</h4>
            <div className="space-y-3">
              {groups.map((g) => {
                const canRetry = ["failed", "failed_permanent"].includes(g.transfer_status) && o.payment_status === "paid";
                return (
                  <div key={g.retailer_id} className="border border-slate-200 rounded-xl p-3" data-testid={`order-group-${g.retailer_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold text-slate-800 flex items-center gap-1.5 min-w-0"><Store className="w-4 h-4 text-emerald-700 shrink-0" /><span className="truncate">{names[g.retailer_id] || "Retailer"}</span></div>
                      <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 shrink-0 ${TRANSFER_STYLE[g.transfer_status] || "bg-slate-100"}`} data-testid={`transfer-state-${g.retailer_id}`}>{TRANSFER_LABEL[g.transfer_status] || g.transfer_status}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{g.items.map((i) => `${i.title} (${i.room_name})`).join(" · ")}</div>
                    <table className="w-full text-sm mt-2">
                      <tbody>
                        <Row k="Sale amount" v={gbp(g.sale)} td />
                        <Row k="Commission (before fees)" v={`− ${gbp(g.commission)}`} td muted />
                        <Row k="Retailer proceeds" v={gbp(g.payout_gbp)} td bold />
                      </tbody>
                    </table>
                    {g.transfer_error && <p className="text-[11px] text-red-600 mt-1">Last error: {g.transfer_error}</p>}
                    {g.transfer_status === "reversed" && <p className="text-[11px] text-slate-500 mt-1">Refunded — transfer reversed.</p>}
                    {canRetry && (
                      <button
                        onClick={() => retry(g.retailer_id)}
                        disabled={retrying === g.retailer_id}
                        className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold py-2 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid={`retry-transfer-${g.retailer_id}`}
                      >
                        {retrying === g.retailer_id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        {retrying === g.retailer_id ? "Retrying…" : "Retry transfer"}
                      </button>
                    )}
                    {g.transfer_status === "paid" && ["paid", "partially_refunded"].includes(o.payment_status) && (
                      <button
                        onClick={() => doRefund(g.retailer_id)}
                        disabled={refunding === g.retailer_id}
                        className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-lg border border-red-300 bg-white hover:bg-red-50 text-red-700 font-semibold py-2 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid={`refund-retailer-${g.retailer_id}`}
                      >
                        {refunding === g.retailer_id ? "Refunding…" : `Refund this retailer (${gbp(g.sale)})`}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Refunds (full order) — reuses the proven refund endpoint */}
          {["paid", "partially_refunded"].includes(o.payment_status) && (
            <section data-testid="refund-section">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Refund</h4>
              <button
                onClick={() => doRefund("full")}
                disabled={refunding === "full"}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="refund-full"
              >
                {refunding === "full" ? "Refunding full order…" : `Refund full order (${gbp(o.total_gbp)})`}
              </button>
              <p className="text-[11px] text-slate-400 mt-1">TEST mode. Refunds the customer and reverses the matching retailer transfer(s). Use per-retailer buttons above for a partial refund.</p>
            </section>
          )}

          {/* Email delivery results */}
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><Mail className="w-4 h-4" /> Email delivery</h4>
            {emailResults.length === 0 ? (
              <p className="text-sm text-slate-400" data-testid="email-results-empty">No paid-order emails recorded yet.</p>
            ) : (
              <div className="space-y-1.5" data-testid="email-results">
                {emailResults.map((e, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600 truncate">{e.to === "customer" ? "Customer confirmation" : `Retailer · ${names[e.to] || e.to}`}</span>
                    {e.accepted ? (
                      <span className="text-emerald-700 inline-flex items-center gap-1 text-xs"><CheckCircle2 className="w-4 h-4" /> Delivered</span>
                    ) : (
                      <span className="text-red-600 text-xs" title={e.error || ""}>Failed{e.error ? `: ${String(e.error).slice(0, 40)}` : ""}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v, bold, muted, td }) {
  if (td) {
    return (
      <tr>
        <td className={`py-0.5 ${muted ? "text-slate-500" : "text-slate-600"}`}>{k}</td>
        <td className={`py-0.5 text-right ${bold ? "font-bold text-[#0F3D2E]" : muted ? "text-slate-500" : "font-semibold text-slate-800"}`}>{v}</td>
      </tr>
    );
  }
  return (
    <div className="flex items-center justify-between">
      <span className={muted ? "text-slate-500" : "text-slate-600"}>{k}</span>
      <span className={bold ? "font-bold text-slate-900" : muted ? "text-slate-500" : "font-semibold text-slate-800"}>{v}</span>
    </div>
  );
}
